import { useEffect, useState } from 'react';

export const useForm = (initialState = {}, initialErrors = {}, initialValidations = {}, onSubmit = () => { }) => {
    const [values, setValues] = useState(initialState);
    const [errors, setErrors] = useState(initialErrors);
    const [validations] = useState(initialValidations);
    const [validated, setValidated] = useState();

    useEffect(() => {
        const validateDictionary = {};
        Object.keys(initialValidations).forEach(k => validateDictionary[k] = false);
        setValidated(validateDictionary);
    }, []);

    const reset = () => {
        setValues(initialState);
        setErrors(initialErrors);
    };

    const customUpdate = (update) => setValues({ ...values, ...update });

    const changeHandler = ({ target: { name, value } }) => {
        const newState = { ...values, [name]: value };
        setValues(newState);
    };

    const validate = ({ target: { value, name } }) => {
        const isValid = validations[[name]](value, values);

        let updatedErrors = { ...errors };

        if (isValid === true) {
            delete updatedErrors[[name]];
        } else {
            updatedErrors = { ...errors, ...isValid };
        }
        setErrors({ ...updatedErrors });
        const validatedField = { ...validated, [name]: true };
        setValidated(validatedField);
        return isValid;
    };

    const isValid = () => Object.keys(errors).length === 0 && Object.keys(validated).filter(v => !validated[v]).length === 0;

    const submitHandler = (event) => {
        event.preventDefault();
        let isFormValid = true;
        let errors = {};
        let validated = [];
        for (const key in validations) {
            const isValid = validations[[key]](values[[key]], values);
            isFormValid = isFormValid && isValid === true;
            if (isValid !== true) {
                errors = { ...errors, ...isValid };
                validated = { ...validated, [key]: true };
            }
        }
        setErrors(errors);
        setValidated(validated);

        if (isFormValid === true) {
            onSubmit(values);
        }
    };

    const setCustomError = (name, error) => setErrors({ ...errors, [name]: error });

    return { values, reset, errors, isValid, customUpdate, changeHandler, submitHandler, validate, setCustomError };
};

export const validators = {
    hasAmount: (value) => /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/.test(value),
    hasPercentage: (value) => /(^100(\.0{1,2})?$)|(^([1-9]([0-9])?|0)(\.[0-9]{1,2})?$)/.test(value),
    hasNumber: (value) => value != null && /^(\s*|\d+)$/.test(value),
    hasValue: (value) => value != null && value.trim().length > 0,
    isValidEmail: (value) => (
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value)
    ),
    validatePassword: (value) => {
        const lowerCaseRegex = /[a-z]/g;
        const upperCaseRegex = /[A-Z]/g;
        const numbersRegex = /[0-9]/g;
        const spacesRegex = /\s/g;
        const specialCharacterRegex = /^(?=.*[!"#$%&()*+,\-./;<=>?@[\]^_`{|}~])[\w!"#$%&()*+,\-./;<=>?@[\]^_`{|}~]/g;
        const passwordRules = {
            length: {
                isValid: (value) => !!value && value.length >= 9 && value.length <= 20,
                error: `Password must be between 9 and 20 characters long`
            },
            lowerCase: {
                isValid: (value) => !!value && value.match(lowerCaseRegex) !== null,
                error: `Password needs at least 1 lowercase letter`
            },
            upperCase: {
                isValid: (value) => !!value && value.match(upperCaseRegex) !== null,
                error: `Password needs at least 1 uppercase letter`
            },
            number: {
                isValid: (value) => !!value && value.match(numbersRegex) !== null,
                error: `Password needs at least 1 number`
            },
            nospaces: {
                isValid: (value) => value.match(spacesRegex) === null,
                error: `Password cannot use spaces`
            },
            special: {
                isValid: (value) => !!value && value.match(specialCharacterRegex) !== null,
                error: `Password needs at least 1 special character (like @, $, or %)`
            }
        };
        let isValid = true;
        const errors = [];
        Object.keys(passwordRules).forEach((r) => {
            const isRuleValid = passwordRules[r].isValid(value);
            isValid = isValid && isRuleValid;
            if (!isRuleValid) errors.push(passwordRules[r].error);
        });
        return isValid || { password: errors };
    }
};
